import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, Play } from "lucide-react";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/3c26e507-38ce-4d09-be4d-b859e821e069/images/e86088690f425bc10b2461135040103e8d76a5de1c8c216c191e7c47b81e39de.png";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full flex items-center pt-28 pb-20 overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt=""
          className="w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/65 to-[#0A0A0A]" />
        <div className="absolute inset-0 bg-tech-grid" />
      </div>

      {/* Glows */}
      <div className="absolute -top-32 -left-32 h-[420px] w-[420px] rounded-full bg-[#FF6B00]/30 blur-[120px] animate-pulse-slow" />
      <div className="absolute -bottom-32 -right-32 h-[420px] w-[420px] rounded-full bg-[#00FF66]/20 blur-[120px] animate-pulse-slow" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 px-4 h-9 rounded-full border border-white/15 bg-white/5 backdrop-blur-md text-xs uppercase tracking-[0.25em] text-gray-300"
          data-testid="hero-eyebrow"
        >
          <Sparkles className="h-3.5 w-3.5 text-[#FF6B00]" />
          3D Software Studio · Belo Horizonte, MG, Brazil
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-6 max-w-5xl text-4xl sm:text-6xl lg:text-7xl xl:text-[88px] font-black leading-[0.95] tracking-tighter"
          data-testid="hero-headline"
        >
          Transforming ideas
          <br />
          into <span className="text-gradient">3D reality.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="mt-7 max-w-2xl text-base sm:text-lg text-gray-300 leading-relaxed"
          data-testid="hero-subheadline"
        >
          AnimaSoftware engineers high-performance 3D software, real-time
          experiences, and immersive VR/AR products — custom-built for studios,
          industry leaders, and bold brands ready to ship the future.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4"
        >
          <a
            href="#contact"
            data-testid="hero-cta-primary"
            className="group inline-flex items-center justify-center gap-2 px-7 h-14 rounded-full bg-gradient-to-r from-[#FF6B00] to-[#00FF66] text-black font-semibold text-sm sm:text-base shadow-[0_0_40px_-10px_rgba(255,107,0,0.6)] hover:-translate-y-0.5 transition-all duration-300"
          >
            Start Your Project
            <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href="#services"
            data-testid="hero-cta-secondary"
            className="group inline-flex items-center justify-center gap-2 px-7 h-14 rounded-full border border-white/15 bg-white/5 text-white font-medium text-sm sm:text-base backdrop-blur-md hover:bg-white/10 transition-all duration-300"
          >
            <Play className="h-4 w-4 text-[#00FF66]" />
            Explore Capabilities
          </a>
        </motion.div>

        {/* Stats strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.55 }}
          className="mt-16 sm:mt-24 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden"
          data-testid="hero-stats"
        >
          {[
            { k: "120+", v: "3D builds shipped" },
            { k: "60 fps", v: "Real-time pipelines" },
            { k: "VR/AR", v: "Immersive products" },
            { k: "12+", v: "Industries served" },
          ].map((s) => (
            <div
              key={s.v}
              className="bg-[#0A0A0A] px-5 py-6 sm:p-7 hover:bg-white/[0.03] transition-colors"
            >
              <div className="font-[Unbounded] text-2xl sm:text-3xl font-bold tracking-tight text-white">
                {s.k}
              </div>
              <div className="mt-2 text-xs uppercase tracking-[0.2em] text-gray-400">
                {s.v}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
